import '@/styles/_index.scss';

import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';

import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import { ModalManagerProvider } from '@/components/global/modal/ModalManagerProvider';
import { readCookie } from '@/components/table/DataTable/util';
import defaultMetaInfo from '@/constants/defaultMetaInfo';
import { getUrlWithHost } from '@/helpers/url';
import { useI18n } from '@/i18n';
import { Channel } from '@/models/channelModels';
import {
  doCompanyFetch,
  doCompanyFetchError,
  doUserSSOFetch,
  doUserSSOFetchError,
  getProfileData,
  setPortalChannel,
} from '@/redux/auth/actions';
import { persistor, wrapper } from '@/redux/store';
import apiService, { envVars } from '@/services/ApiService';
import LocalDataService from '@/services/LocalDataService';
import { CommonServerSideProps } from '@/util/getServerSideProps';
import CompanyChecker from '@/wrappers/companyChecker/CompanyChecker';
import LayoutWrapper from '@/wrappers/layout/LayoutWrapper';
import LocalizeScript from '@/wrappers/scripts/LocalizeScript';
import SegmentScript, { SegmentPageListener } from '@/wrappers/scripts/SegmentScript';

import { AmbassadorError, ServerStatus } from '../models/globalModels';

export interface AppPropsWithServer extends AppProps<CommonServerSideProps> {}

function MainApp({ Component, ...rest }: AppPropsWithServer) {
  const { company, companyError, userError, user, host, nonce } = rest.pageProps;
  const { init: initI18n } = useI18n();
  const router = useRouter();
  const { store, props } = wrapper.useWrappedStore(rest);
  const [nonceState, setNonceState] = React.useState<string | undefined>(nonce! || undefined);

  useEffect(() => {
    if (nonce) {
      setNonceState(nonce);
    }
  }, [nonce]);

  // Set portal channel from cookie
  useEffect(() => {
    const fromCookie = readCookie('portal_channel');
    const channel = fromCookie === Channel.STOREFRONT ? Channel.STOREFRONT : Channel.DEFAULT;

    store.dispatch(setPortalChannel(channel));
  }, [store]);

  const isStorefrontRoute =
    typeof router?.pathname === 'string' && (router.pathname === '/storefront' || router.pathname.startsWith('/storefront'));

  const isAppRoute = typeof router?.pathname === 'string' && router.pathname.startsWith('/app/');

  const portalChannelCookie = readCookie('portal_channel');
  const isStorefrontChannel = portalChannelCookie === Channel.STOREFRONT;
  const hasStorefrontUidCookie = Boolean(readCookie('portal_company_uid'));

  const isGenericStorefront = isStorefrontRoute && !host;

  // bypass CompanyChecker in generic storefront AND storefront-channel /app refreshes
  const bypassCompanyChecker = isGenericStorefront || (isStorefrontChannel && isAppRoute && !host && hasStorefrontUidCookie);

  const metaInfo = company?.customization_uid
    ? {
        head_title: company?.customization_uid.head_title || defaultMetaInfo.metaTitle,
        head_description: company?.customization_uid.head_description || defaultMetaInfo.metaDescription,
        favicon: company?.customization_uid.favicon || defaultMetaInfo.faviconURL,
      }
    : {
        head_title: defaultMetaInfo.metaTitle,
        head_description: defaultMetaInfo.metaDescription,
        favicon: defaultMetaInfo.faviconURL,
      };

  React.useEffect(() => {
    require('bootstrap/dist/js/bootstrap.bundle.min.js');
  }, []);

  React.useEffect(() => {
    const { auth } = store.getState();

    const canHydrateStorefront = Boolean(isStorefrontChannel && company);
    const hostFromCompany = company?.portal_custom_url || '';

    if (!userError && !companyError && company && (host || canHydrateStorefront)) {
      // check if user is sso!
      if (user) store.dispatch(doUserSSOFetch(user));
      // Detecting successfull call and save data
      LocalDataService.saveCompanyId(company.uid);

      if (host) {
        LocalDataService.savePortalHost(host);
      } else if (isStorefrontChannel && hostFromCompany) {
        LocalDataService.savePortalHost(hostFromCompany);
      }

      store.dispatch(doCompanyFetch(company));
      store.dispatch(getProfileData());
    } else {
      const { companyStatus } = auth;
      if (companyStatus != ServerStatus.FETCH) {
        // Detecting invalid company
        // the error page rendered is inside <CompanyChecker />
        if (userError) {
          // User Error
          const error: AmbassadorError = {
            message: userError ?? 'Error with the user',
            error: {
              cause: 'invalid-user',
              message: userError ?? 'Error with the user',
              name: 'invalid-user',
            },
            cause: 'invalid-user',
            source: 'serverside-logic',
            stack: 'serverside-logic',
            apiResponse: {},
          };
          store.dispatch(doUserSSOFetchError(error));

          // Avoid redirect loops for storefront channel generic pages
          if (!isStorefrontChannel || host) {
            window.location.href = getUrlWithHost('/login', envVars.checkHost);
          }
        } else if (companyError) {
          // company Error
          const error: AmbassadorError = {
            message: companyError ?? 'Error with the company',
            error: {
              cause: 'invalid-company',
              message: companyError ?? 'Error with the company',
              name: 'invalid-company',
            },
            cause: 'invalid-company',
            source: 'serverside-logic',
            stack: 'serverside-logic',
            apiResponse: {},
          };
          store.dispatch(doCompanyFetchError(error));
        }
      }
      // show error, invalid host
    }
  }, [company, companyError, host, isStorefrontChannel]);

  initI18n();

  const { getCurrentLanguage } = useI18n();

  useEffect(() => {
    apiService.setLanguage(getCurrentLanguage());
  }, []);

  const page = (
    <LayoutWrapper company={company ?? undefined} nonce={nonceState}>
      <Component {...props} nonce={nonceState} />
      <SegmentPageListener />
      {nonceState && <LocalizeScript nonce={nonceState} />}
    </LayoutWrapper>
  );

  return (
    <>
      <Head>
        <title>{metaInfo.head_title}</title>
        <meta key="metaDescription" content={metaInfo.head_description} name="description" />
        <meta key="metaOgTitle" content={metaInfo.head_title} property="og:title" />
        <meta key="metaOgDescription" content={metaInfo.head_description} property="og:description" />
        <meta key="metaOgImage" content={metaInfo.favicon} property="og:image" />
        <link key="linkIcon" href={metaInfo.favicon} rel="icon" />
        <link key="linkAppleTouchIcon" href={metaInfo.favicon} rel="apple-touch-icon" sizes="180x180" />
      </Head>

      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {/* bypass CompanyChecker in generic storefront */}
          {bypassCompanyChecker ? (
            <ModalManagerProvider>{page}</ModalManagerProvider>
          ) : (
            <CompanyChecker>
              <ModalManagerProvider>{page}</ModalManagerProvider>
            </CompanyChecker>
          )}
        </PersistGate>
      </Provider>

      <SegmentScript />
    </>
  );
}

export default MainApp;
